import {
  ArrowRight,
  SkipForward,
  Volume2,
  Upload,
  ArrowLeft,
  Loader,
} from "lucide-react";
import PracticeRecording from "./PracticeRecording";
import { useProfileContext } from "../../../context/ProfileContext";
import WordImage from "./WordImage";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

type Props = {
  recordingRef: React.RefObject<any>;
  onNext: () => void;
  onSkip: () => void;
  onSubmitAudio: () => void;
  isUploading: boolean;
  uploadStatus: string;
  audioSubmitted: boolean;
};

const AudioPracticePage = ({
  recordingRef,
  onNext,
  onSkip,
  onSubmitAudio,
  isUploading,
  uploadStatus,
  audioSubmitted,
}: Props) => {
  const { currentWord } = useProfileContext();
  const router = useNavigate();

  const speakAudioBlob = (file: Blob) => {
    const audioUrl = URL.createObjectURL(file);
    const audio = new Audio(audioUrl);
    audio.play();
    audio.onended = () => {
      URL.revokeObjectURL(audioUrl);
    };
  };

  if (!currentWord) {
    return (
      <>
        <p>No word selected for practice.</p>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-400 via-purple-300 to-pink-200 p-4 flex items-center justify-center">
      <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 max-w-2xl w-full transform hover:scale-105 transition-all duration-300">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-blue-800 mb-4 animate-bounce">
            🎤 Practice Speaking! 🎤
          </h1>
          <p className="text-lg text-gray-600 mb-4">
            Step 1 of 2: Learn to say the word
          </p>
        </div>

        {/* Word Info */}
        <WordImage />

        {/* Word Display */}
        <div className="flex gap-4 mb-6 sm:flex-row flex-col">
          <button
            onClick={() => speakAudioBlob(currentWord.audio_english!)}
            className="flex-1 bg-gradient-to-r from-orange-400 to-yellow-400 text-white font-bold py-4 px-6 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
          >
            <span className="text-2xl">{currentWord.english}</span>
            <Volume2 className="w-6 h-6" />
          </button>

          <button
            onClick={() => speakAudioBlob(currentWord.audio_telugu!)}
            className="flex-1 bg-gradient-to-r from-green-400 to-blue-400 text-white font-bold py-4 px-6 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
          >
            <span className="text-2xl">{currentWord.telugu}</span>
            <Volume2 className="w-6 h-6" />
          </button>
        </div>

        {/* Recording Component */}
        <div className="mb-6">
          <PracticeRecording
            ref={recordingRef}
            key={currentWord.english + "recording"}
          />
        </div>

        {/* Submit Audio Button */}
        <div className="mb-4">
          <button
            onClick={onSubmitAudio}
            disabled={isUploading || audioSubmitted}
            className={`w-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-bold py-4 px-6 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2 ${
              isUploading || audioSubmitted
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          >
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                Submitting Audio...
              </>
            ) : audioSubmitted ? (
              <>
                <span className="text-2xl">✅</span>
                Audio Submitted!
              </>
            ) : (
              <>
                <Upload className="w-6 h-6" />
                Submit Audio Recordings
              </>
            )}
          </button>
        </div>

        {/* Upload Status */}
        {uploadStatus && (
          <div
            className={`mb-4 p-3 rounded-xl font-semibold text-center ${
              uploadStatus.includes("✅")
                ? "bg-green-100 text-green-700 border-2 border-green-300"
                : uploadStatus.includes("❌")
                ? "bg-red-100 text-red-700 border-2 border-red-300"
                : "bg-blue-100 text-blue-700 border-2 border-blue-300"
            }`}
          >
            {uploadStatus}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-4">
          <button
            onClick={onSkip}
            className="flex-1 bg-gradient-to-r from-gray-400 to-gray-500 text-white font-bold py-4 px-6 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
          >
            <SkipForward className="w-6 h-6" />
            Skip Word
          </button>

          <button
            onClick={onNext}
            className="flex-1 bg-gradient-to-r from-emerald-400 to-teal-400 text-white font-bold py-4 px-6 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
          >
            Next: Draw It!
            <ArrowRight className="w-6 h-6" />
          </button>
        </div>
        <button
          onClick={() => router("/profile")}
          disabled={isUploading || audioSubmitted}
          className={`w-full mt-4 bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-bold py-4 px-6 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2`}
        >
          <ArrowLeft className="w-6 h-6" />
          Back To Home
        </button>

        {/* Decorative Elements */}
        <div className="absolute top-4 left-4 text-4xl animate-spin">🌟</div>
        <div className="absolute top-4 right-4 text-4xl animate-bounce">🎈</div>
        <div className="absolute bottom-4 left-4 text-4xl animate-pulse">
          🌈
        </div>
        <div className="absolute bottom-4 right-4 text-4xl animate-bounce">
          🎯
        </div>
      </div>
    </div>
  );
};

export default AudioPracticePage;
